import { Box, Flex, Heading, HStack, Icon, Text, useClipboard } from "@highoutput/ui-components";
import Link from "next/link";
import { useRouter } from "next/router";
import type { FC } from "react";
import { InView } from "react-intersection-observer";
import Navbar from "~/components/navbar";
import { Break } from "../ethics";
import CopyLinkIcon from "../icons/copy-link";
import FacebookIcon from "../icons/facebook";
import LinkedInIcon from "../icons/linked-in";
import TwitterIcon from "../icons/twitter";
import Footer from "../landing/v2/footer";
import For from "../utilities/for";
import { PrivacyPolicyMockData } from "./mockData";
const PrivacyPolicy: FC = () => {
  const router = useRouter();

  const { hasCopied, onCopy } = useClipboard("https://advante.co/privacy-policy");

  return (
    <Flex minHeight="100vh" direction="column">
      <Navbar />

      <InView triggerOnce delay={200}>
        {({ ref, inView }) => (
          <>
            <Box
              ref={ref}
              textAlign="center"
              py={{ base: "48px", lg: "64px" }}
              maxWidth={{
                base: "375px",
                lg: "1024px",
                xl: "1120px",
              }}
              mx={{ base: "auto", lg: "24px", xl: "auto" }}
              width={{ base: "93%" }}
              sx={{
                opacity: { base: 1, lg: 0 },
                transform: { base: "translateY(0vh)", lg: "translateY(10vh)" },
                transition: "transform 600ms ease-out, opacity 600ms ease-out",
                willChange: "transform, opacity",
                ...(inView && {
                  opacity: 1,
                  transform: "none",
                }),
              }}
            >
              <Text variant={{ base: "14/20", lg: "16/24" }} color="primary.blue_80">
                Current as of January 04, 2023
              </Text>

              <Heading
                variant={{ base: "40/44", lg: "56/64" }}
                fontWeight={400}
                color="rgb(13,48,80)"
                fontFamily="Georgia"
                mt="12px"
                mb={{ base: "1rem", lg: "24px" }}
              >
                Privacy Policy
              </Heading>

              <Text
                variant={{ base: "18/28", lg: "20/32" }}
                color="rgba(13,48,80,0.8)"
                fontFamily="'Inter', sans-serif"
              >
                This Privacy Policy describes Our policies and procedures on the collection, use and
                disclosure of <Break /> Your information when You use the Service and tells You
                about Your privacy rights <Break /> and how the law protects You.
                <br />
                <br />
                We use Your Personal data to provide and improve the Service. By using the Service,
                You agree to <Break /> the collection and use of information in accordance with this
                Privacy Policy.
              </Text>
            </Box>
          </>
        )}
      </InView>

      <Box
        maxWidth={{
          base: "auto",
          lg: "1024px",
          xl: "1120px",
        }}
        mx={{ base: "1rem", lg: "auto" }}
      >
        <Flex gap={{ base: "64px" }} marginInline={{ base: 0, lg: "24px", xl: 0 }}>
          <InView triggerOnce delay={200}>
            {({ ref, inView }) => (
              <Box
                borderTop="1px solid"
                borderColor="rgba(240,237,236,0.4)"
                w={{ lg: "35%", xl: "30%" }}
                h={{ lg: 625 }}
                display={{ base: "none", lg: "block" }}
                position="sticky"
                top={"50px"}
                ref={ref}
                sx={{
                  opacity: { base: 1, lg: 0 },
                  transform: { base: "translateY(0vh)", lg: "translateY(10vh)" },
                  transition: "transform 600ms ease-out, opacity 600ms ease-out",
                  willChange: "transform, opacity",
                  ...(inView && {
                    opacity: 1,
                    transform: "none",
                  }),
                }}
                fontFamily="'Inter',sans-serif"
              >
                <Flex
                  direction={"column"}
                  overflowY="auto"
                  css={{
                    "::-webkit-scrollbar": {
                      display: "none",
                    },
                  }}
                  height={"90vh"}
                >
                  <Text variant="16/24" color="v2.blue.100" mt="17px" mb="1rem" fontWeight={600}>
                    Table of contents
                  </Text>

                  <Box borderBottom="1px solid" borderColor="rgba(240,237,236,0.4)">
                    <For each={PrivacyPolicyMockData}>
                      {({ heading }) => {
                        const active =
                          router.asPath.substr(16, 40) ===
                          heading.toLowerCase().split(" ").join("-");

                        return (
                          <>
                            <Link href={`#${heading.toLowerCase().split(" ").join("-")}`}>
                              <Text
                                variant="16/24"
                                fontWeight={400}
                                my="12px"
                                color={active ? "rgb(13,48,80)" : "rgba(13,48,80,0.6)"}
                                cursor="pointer"
                                _hover={{ color: "rgb(13,48,80)" }}
                              >
                                {heading}
                              </Text>
                            </Link>
                          </>
                        );
                      }}
                    </For>
                  </Box>

                  <HStack spacing={3}>
                    <Box
                      as="a"
                      role="navigation"
                      rel="noreferrer noopener"
                      target="_blank"
                      display="flex"
                      padding="10px"
                      borderRadius="10px"
                      border="solid 2px"
                      borderColor="rgba(240,237,236,0.4)"
                      my="1rem"
                      onClick={onCopy}
                      cursor={"pointer"}
                      _hover={{ background: "rgba(16, 24, 40, 0.05)" }}
                    >
                      {hasCopied && <Text color="rgba(13,48,80,0.6)">Copied!</Text>}
                      {!hasCopied && (
                        <Icon
                          as={CopyLinkIcon}
                          width={{
                            base: "20px",
                            lg: "24px",
                          }}
                          height={{
                            base: "20px",
                            lg: "24px",
                          }}
                        />
                      )}
                    </Box>
                    <For
                      each={[
                        [
                          "twitter",
                          "https://twitter.com/intent/tweet?url=https://advante.co/privacy-policy",
                        ],
                        [
                          "facebook",
                          "https://www.facebook.com/sharer/sharer.php?u=https://advante.co/privacy-policy",
                        ],
                        [
                          "linkedIn",
                          "https://www.linkedin.com/sharing/share-offsite/?url=https://advante.co/privacy-policy",
                        ],
                      ]}
                    >
                      {([social, url]) => {
                        const SocialIcon = {
                          twitter: TwitterIcon,
                          facebook: FacebookIcon,
                          linkedIn: LinkedInIcon,
                        }[social];

                        return (
                          <Box
                            as="a"
                            role="navigation"
                            aria-label={`Go to advante's ${social} account`}
                            rel="noreferrer noopener"
                            href={url}
                            target="_blank"
                            display="flex"
                            padding="10px"
                            borderRadius="10px"
                            border="solid 2px"
                            borderColor="rgba(240,237,236,0.4)"
                            color="rgba(240,237,236)"
                            my="1rem"
                            _hover={{ background: "rgba(16, 24, 40, 0.05)" }}
                          >
                            <Icon
                              as={SocialIcon}
                              fill="rgba(13, 48, 80, 0.4)"
                              width={{
                                base: "20px",
                                lg: "24px",
                              }}
                              height={{
                                base: "20px",
                                lg: "24px",
                              }}
                            />
                          </Box>
                        );
                      }}
                    </For>
                  </HStack>
                </Flex>
              </Box>
            )}
          </InView>

          <Box mb="64px" w={{ base: "100%", lg: "65%", xl: "70%" }}>
            <For each={PrivacyPolicyMockData}>
              {({ heading, content }) => (
                <Box id={`${heading.toLowerCase().split(" ").join("-")}`}>
                  <InView triggerOnce delay={200}>
                    {({ ref, inView }) => (
                      <Box
                        ref={ref}
                        gap={{
                          base: "44px",
                          lg: "100px",
                        }}
                        alignItems="center"
                        sx={{
                          opacity: 0,
                          transform: "translateY(10vh)",
                          transition: "transform 600ms ease-out, opacity 600ms ease-out",
                          willChange: "transform, opacity",
                          ...(inView && {
                            opacity: 1,
                            transform: "none",
                          }),
                        }}
                      >
                        <Text
                          variant={{
                            base:
                              heading === "Disclosure of Your Personal Data" ? "32/36" : "24/28",
                            lg: "32/36",
                          }}
                          fontWeight={400}
                          mb={{ base: "20px", lg: "24px" }}
                          fontFamily="Georgia"
                          letterSpacing={"-0.02em"}
                          wordBreak={"break-word"}
                          w={{
                            base: heading === "Tracking Technologies and Cookies" ? "75%" : "none",
                          }}
                        >
                          {heading}
                        </Text>

                        <Box
                          marginTop="12px"
                          mb={{ base: "40px", lg: "48px" }}
                          fontSize={{ base: "16px", lg: "18px" }}
                          fontFamily="'Inter',sans-serif"
                          dangerouslySetInnerHTML={{
                            __html: content,
                          }}
                        />
                      </Box>
                    )}
                  </InView>
                </Box>
              )}
            </For>
            <HStack
              spacing={3}
              display={{ base: "flex", lg: "none" }}
              borderTop="1px solid"
              borderColor="rgba(240,237,236,0.4)"
              height={"80px"}
            >
              <Box
                as="a"
                role="navigation"
                rel="noreferrer noopener"
                target="_blank"
                display="flex"
                padding="8px"
                borderRadius="10px"
                border="solid 2px"
                borderColor="rgba(240,237,236,0.4)"
                my="1rem"
                onClick={onCopy}
                cursor={"pointer"}
                _hover={{ background: "rgba(16, 24, 40, 0.05)" }}
              >
                {hasCopied && <Text color="rgba(13,48,80,0.6)">Copied!</Text>}
                {!hasCopied && (
                  <Icon
                    as={CopyLinkIcon}
                    width={{
                      base: "20px",
                      lg: "24px",
                    }}
                    height={{
                      base: "20px",
                      lg: "24px",
                    }}
                  />
                )}
              </Box>
              <For
                each={[
                  [
                    "twitter",
                    "https://twitter.com/intent/tweet?url=https://advante.co/privacy-policy",
                  ],
                  [
                    "facebook",
                    "https://www.facebook.com/sharer/sharer.php?u=https://advante.co/privacy-policy",
                  ],
                  [
                    "linkedIn",
                    "https://www.linkedin.com/sharing/share-offsite/?url=https://advante.co/privacy-policy",
                  ],
                ]}
              >
                {([social, url]) => {
                  const SocialIcon = {
                    twitter: TwitterIcon,
                    facebook: FacebookIcon,
                    linkedIn: LinkedInIcon,
                  }[social];

                  return (
                    <Box
                      as="a"
                      role="navigation"
                      aria-label={`Go to advante's ${social} account`}
                      rel="noreferrer noopener"
                      href={url}
                      target="_blank"
                      display="flex"
                      padding="10px"
                      borderRadius="10px"
                      border="solid 2px"
                      borderColor="rgba(240,237,236,0.4)"
                      my="1rem"
                      _hover={{ background: "rgba(16, 24, 40, 0.05)" }}
                    >
                      <Icon
                        as={SocialIcon}
                        fill="rgba(13, 48, 80, 0.4)"
                        width={{
                          base: "20px",
                          lg: "24px",
                        }}
                        height={{
                          base: "20px",
                          lg: "24px",
                        }}
                      />
                    </Box>
                  );
                }}
              </For>
            </HStack>
          </Box>
        </Flex>
      </Box>

      <Footer cta />
    </Flex>
  );
};
export default PrivacyPolicy;
